// @ts-nocheck
/**
 * Module list — redesigned to obd3-gui pattern (FillPage + PageHeader +
 * DataTable + tinted row-action buttons + ConfirmDialog).
 * Preserved: multi/single delete + refetch, CSV export, ACL gates.
 * v1 shipped with the Actions column commented out — re-enabled here so
 * Edit / Delete are reachable from the row.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Download, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_MODULE_LIST, API_MODULE_DELETE } from '@/service/url';
import { ROUTES, MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const formatDate = (v: any) =>
  v
    ? new Date(v).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

export default function ModuleList() {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState<any[]>([]);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [pendingBulk, setPendingBulk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [showToast, setShowToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedModules, setSelectedModules] = useState<any[]>([]);
  const [exporting, setExporting] = useState(false);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [
    moduleOperationsForReadPermissionCheck,
    setModuleOperationsForReadPermissionCheck,
  ] = useState<any>({});

  useEffect(() => {
    setLoading(true);
    const user = getUser();
    if (user?.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) {
      const parsed = parseModuleOperations(user.acl_settings.module_operations);
      setLoggedInUserACL(parsed);
      setModuleOperationsForReadPermissionCheck(parsed);
    }
    fetchList();
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const fetchList = () => {
    getApiCall(API_MODULE_LIST)
      .then((resp: any) => {
        const list = (resp || []).map((val: any, idx: number) => ({
          ...val,
          key: idx + 1,
        }));
        setModules(list);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  };

  function onError(err: any) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleCheckboxToggle = (id: any) => {
    setSelectedModules((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedModules.length === modules.length) setSelectedModules([]);
    else setSelectedModules(modules.map((m: any) => m.id));
  };

  const requestSingleDelete = (row: any) => {
    checkAuth(setToastProps, setShowToast);
    setPendingDelete(row);
  };

  const confirmDelete = async () => {
    const ids = pendingBulk
      ? selectedModules
      : pendingDelete
      ? [pendingDelete.id]
      : [];
    if (!ids.length) return;
    setDeleting(true);
    try {
      await postApiCall(API_MODULE_DELETE, { moduleIds: ids });
      toast.success('Module deleted successfully');
      if (pendingBulk) setSelectedModules([]);
      setPendingDelete(null);
      setPendingBulk(false);
      setLoading(true);
      fetchList();
    } catch (err: any) {
      if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
        errorCheck(err, navigate, setToastProps, setShowToast);
      } else {
        toast.error(friendlyApiError(err));
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      exportToCSV({
        fileName: 'module-list',
        data: modules,
        columns: [
          { header: 'Module Name', accessor: 'module_name' },
          {
            header: 'Created Date',
            accessor: (row: any) => formatDate(row.created_at),
          },
        ],
      });
    } finally {
      setExporting(false);
    }
  };

  const canRead = isSuperAdmin;
  const canEdit = isSuperAdmin;
  const canDelete = isSuperAdmin;
  const canAdd = isSuperAdmin;
  const showActions = canEdit || canDelete;

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        id: '_select',
        size: 40,
        enableSorting: false,
        enableHiding: false,
        header: () => (
          <Checkbox
            checked={
              selectedModules.length === modules.length && modules.length > 0
            }
            onCheckedChange={handleSelectAll}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selectedModules.includes(row.original.id)}
            onCheckedChange={() => handleCheckboxToggle(row.original.id)}
            aria-label="Select row"
          />
        ),
      },
      {
        accessorKey: 'module_name',
        header: 'Module Name',
        cell: ({ row }) =>
          canEdit ? (
            <Link
              to={`/${ROUTES.MODULE.EDIT.replace(':id', row.original.id)}`}
              className="font-medium text-foreground hover:underline"
            >
              {row.original.module_name}
            </Link>
          ) : (
            <span className="font-medium text-foreground">
              {row.original.module_name}
            </span>
          ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
    ];

    if (showActions) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            {canEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/${ROUTES.MODULE.EDIT.replace(':id', row.original.id)}`,
                  );
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                title="Edit"
                aria-label="Edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {selectedModules.length === 0 && canDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  requestSingleDelete(row.original);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                title="Delete"
                aria-label="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ),
      });
    }
    return cols;
  }, [selectedModules, modules, canEdit, canDelete, showActions, navigate]);

  if (!canRead) {
    return (
      <FillPage>
        <PageHeader title="Modules" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  if (loading) return <Loader />;

  return (
    <FillPage>
      <PageHeader
        title="Modules"
        description="Master list of application modules used for ACL gating."
        actions={
          <>
            {canDelete && selectedModules.length > 0 && (
              <Button variant="destructive" onClick={() => setPendingBulk(true)}>
                <Trash2 className="h-4 w-4" />
                Delete Selected ({selectedModules.length})
              </Button>
            )}
            {modules.length > 0 && (
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={exporting}
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button onClick={() => navigate(`/${ROUTES.MODULE.CREATE}`)}>
                <Plus className="h-4 w-4" />
                Create Module
              </Button>
            )}
          </>
        }
      />

      <DataTable
        tableId="module.list:v1"
        columns={columns}
        data={modules}
        isLoading={loading}
        searchPlaceholder="Search by name…"
        emptyTitle="No modules"
        emptyDescription="No modules match the current filters."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={showActions ? { right: ['actions'] } : undefined}
        pageSize={10}
        disableExport
      />

      <ConfirmDialog
        open={pendingDelete !== null || pendingBulk}
        onOpenChange={(o) => {
          if (!o) {
            setPendingDelete(null);
            setPendingBulk(false);
          }
        }}
        title={pendingBulk ? 'Delete selected modules?' : 'Delete this module?'}
        description={
          pendingBulk ? (
            <>
              <span className="font-medium text-foreground">
                {selectedModules.length}
              </span>{' '}
              module(s) will be permanently deleted.
            </>
          ) : (
            <>
              Module{' '}
              <span className="font-medium text-foreground">
                {pendingDelete?.module_name}
              </span>{' '}
              will be permanently deleted.
            </>
          )
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
}
